import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { SurveyQuestion, SurveyQuestionType, SurveyTemplateSummary } from '../../types/survey';
import { useSurveyService } from '../../services/surveyService';
import { buildSurveyTemplateWriteDto, normalizeQuestionsForForm } from '../../utils/surveyTemplateWrite';
import { getDefaultScaleLimits, validateQuestionLimitsForTemplate } from '../../utils/surveyQuestions';

const questionTypeOptions: Array<{ value: SurveyQuestionType; label: string }> = [
  { value: 'INSTRUCTION', label: 'Instructivo' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'NUMBER', label: 'Numérica' },
  { value: 'SCALE', label: 'Escala' },
  { value: 'BOOLEAN', label: 'Booleano' },
  { value: 'SINGLE_CHOICE', label: 'Selección única' },
  { value: 'MULTI_CHOICE', label: 'Selección múltiple' }
];

const emptyQuestion = (sortOrder: number): SurveyQuestion => ({
  sortOrder,
  questionText: '',
  questionType: 'TEXT',
  required: false,
  options: []
});

type FormMode = 'create' | 'edit';

const SurveyTemplatesManagement: React.FC = () => {
  const { getSurveyTemplates, getSurveyTemplateById, createSurveyTemplate, replaceSurveyTemplate, patchSurveyTemplate } =
    useSurveyService();
  const [templates, setTemplates] = useState<SurveyTemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [hasClientSurveys, setHasClientSurveys] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [templateVersion, setTemplateVersion] = useState('1');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [questions, setQuestions] = useState<SurveyQuestion[]>([emptyQuestion(1)]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSurveyTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las plantillas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setActive(true);
    setTemplateVersion('1');
    setValidFrom('');
    setValidTo('');
    setQuestions([emptyQuestion(1)]);
    setFormError('');
    setHasClientSurveys(false);
    setEditingTemplateId(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const openCreateForm = () => {
    resetForm();
    setFormMode('create');
    setFormOpen(true);
  };

  const hydrateFormFromDetail = (detail: Awaited<ReturnType<typeof getSurveyTemplateById>>) => {
    setTitle(detail.title);
    setDescription(detail.description || '');
    setActive(detail.active);
    setTemplateVersion(String(detail.templateVersion ?? 1));
    setValidFrom(detail.validFrom || '');
    setValidTo(detail.validTo || '');
    setQuestions(
      detail.questions?.length
        ? normalizeQuestionsForForm(detail.questions)
        : [emptyQuestion(1)]
    );
    setHasClientSurveys(Boolean(detail.hasClientSurveys));
    setEditingTemplateId(detail.id);
  };

  const openEditForm = async (templateId: number) => {
    setFormError('');
    setSuccessMessage('');
    setFormMode('edit');
    setFormOpen(true);
    setLoadingDetail(true);
    try {
      const detail = await getSurveyTemplateById(templateId);
      hydrateFormFromDetail(detail);
    } catch (err) {
      setFormOpen(false);
      setError(err instanceof Error ? err.message : 'No se pudo cargar la plantilla.');
    } finally {
      setLoadingDetail(false);
    }
  };

  const canSave = useMemo(() => {
    if (!title.trim()) return false;
    if (questions.length === 0) return false;
    const hasAnswerable = questions.some((q) => q.questionType !== 'INSTRUCTION');
    if (!hasAnswerable) return false;
    return questions.every((q) => q.questionText.trim().length > 0);
  }, [title, questions]);

  const saveButtonLabel = useMemo(() => {
    if (formMode === 'create') return 'Guardar plantilla';
    return hasClientSurveys ? 'Guardar como nueva versión' : 'Guardar cambios';
  }, [formMode, hasClientSurveys]);

  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion(prev.length + 1)]);

  const removeQuestion = (idx: number) => {
    setQuestions((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((q, i) => ({ ...q, sortOrder: i + 1 }))
    );
  };

  const updateQuestion = (idx: number, patch: Partial<SurveyQuestion>) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== idx) return q;
        const next = { ...q, ...patch };
        if (next.questionType === 'INSTRUCTION') {
          return { ...next, required: false, options: [], minValue: null, maxValue: null };
        }
        return next;
      })
    );
  };

  const handleQuestionTypeChange = (qIdx: number, nextType: SurveyQuestionType, question: SurveyQuestion) => {
    const isChoice = nextType === 'SINGLE_CHOICE' || nextType === 'MULTI_CHOICE';
    const patch: Partial<SurveyQuestion> = {
      questionType: nextType,
      required: nextType === 'INSTRUCTION' ? false : question.required,
      options: isChoice ? question.options : []
    };

    if (nextType === 'SCALE') {
      const defaults = getDefaultScaleLimits();
      patch.minValue = question.minValue ?? defaults.minValue;
      patch.maxValue = question.maxValue ?? defaults.maxValue;
    } else if (nextType === 'NUMBER') {
      patch.minValue = question.questionType === 'NUMBER' ? question.minValue ?? null : null;
      patch.maxValue = question.questionType === 'NUMBER' ? question.maxValue ?? null : null;
    } else {
      patch.minValue = null;
      patch.maxValue = null;
    }

    updateQuestion(qIdx, patch);
  };

  const addOption = (idx: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== idx
          ? q
          : {
              ...q,
              options: [
                ...q.options,
                { sortOrder: q.options.length + 1, label: '', optionValue: '' }
              ]
            }
      )
    );
  };

  const removeOption = (qIdx: number, optIdx: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== qIdx
          ? q
          : {
              ...q,
              options: q.options
                .filter((_, oi) => oi !== optIdx)
                .map((opt, oi) => ({ ...opt, sortOrder: oi + 1 }))
            }
      )
    );
  };

  const updateOption = (qIdx: number, optIdx: number, key: 'label' | 'optionValue', value: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== qIdx
          ? q
          : {
              ...q,
              options: q.options.map((opt, oi) => (oi === optIdx ? { ...opt, [key]: value } : opt))
            }
      )
    );
  };

  const validateForm = (): string | null => {
    const parsedVersion = Number(templateVersion);
    if (Number.isNaN(parsedVersion) || parsedVersion < 1) {
      return 'La versión de plantilla debe ser un número mayor o igual a 1.';
    }

    const answerableCount = questions.filter((q) => q.questionType !== 'INSTRUCTION').length;
    if (answerableCount === 0) {
      return 'La plantilla debe incluir al menos una pregunta respondible (no solo instructivos).';
    }

    const invalidChoices = questions.find((q) => {
      const isChoice = q.questionType === 'SINGLE_CHOICE' || q.questionType === 'MULTI_CHOICE';
      if (!isChoice) return false;
      if (q.options.length === 0) return true;
      return q.options.some((opt) => !opt.label.trim() || !opt.optionValue.trim());
    });
    if (invalidChoices) {
      return 'Las preguntas de selección requieren opciones con etiqueta y valor.';
    }

    for (const question of questions) {
      const limitError = validateQuestionLimitsForTemplate(question);
      if (limitError) return limitError;
    }

    return null;
  };

  const handleSaveTemplate = async () => {
    setFormError('');
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const parsedVersion = Number(templateVersion);
    const payload = buildSurveyTemplateWriteDto({
      title,
      description: description.trim() || null,
      active,
      templateVersion: parsedVersion,
      validFrom: validFrom || null,
      validTo: validTo || null,
      questions
    });

    try {
      setSaving(true);

      if (formMode === 'create') {
        await createSurveyTemplate(payload);
        setSuccessMessage('Plantilla creada correctamente.');
        closeForm();
      } else if (editingTemplateId != null) {
        const result = await replaceSurveyTemplate(editingTemplateId, payload);

        if (result.status === 201 && result.createdAsNewVersion) {
          hydrateFormFromDetail(result.detail);
          setSuccessMessage(
            `Se creó la versión ${result.detail.templateVersion ?? ''} (id ${result.detail.id}). Los registros ya capturados conservan la plantilla anterior.`
          );
        } else {
          hydrateFormFromDetail(result.detail);
          setSuccessMessage('Plantilla actualizada correctamente.');
        }
      }

      await loadTemplates();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar la plantilla.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template: SurveyTemplateSummary) => {
    try {
      await patchSurveyTemplate(template.id, { active: !template.active });
      await loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar el estado.');
    }
  };

  const renderQuestionFields = () => (
    <>
      <Typography variant="h6">Preguntas</Typography>
      <Stack spacing={1.5}>
        {questions.map((question, qIdx) => {
          const isInstruction = question.questionType === 'INSTRUCTION';
          const isChoice =
            question.questionType === 'SINGLE_CHOICE' || question.questionType === 'MULTI_CHOICE';
          const isNumber = question.questionType === 'NUMBER';
          const isScale = question.questionType === 'SCALE';
          return (
            <Paper key={`q-${qIdx}`} variant="outlined" sx={{ p: 1.5 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    multiline={isInstruction}
                    minRows={isInstruction ? 2 : 1}
                    label={isInstruction ? `Instructivo #${qIdx + 1}` : `Pregunta #${qIdx + 1}`}
                    value={question.questionText}
                    onChange={(e) => updateQuestion(qIdx, { questionText: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={question.questionType}
                            onChange={(e) =>
                              handleQuestionTypeChange(qIdx, e.target.value as SurveyQuestionType, question)
                            }
                    >
                      {questionTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {!isInstruction && (
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={question.required}
                          onChange={(e) => updateQuestion(qIdx, { required: e.target.checked })}
                        />
                      }
                      label="Obligatoria"
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton color="error" onClick={() => removeQuestion(qIdx)} disabled={questions.length <= 1}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>

              {(isNumber || isScale) && (
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Valor mínimo"
                      required={isScale}
                      inputProps={isScale ? { step: 1 } : undefined}
                      value={question.minValue ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateQuestion(qIdx, { minValue: val === '' ? null : Number(val) });
                      }}
                      helperText={isScale ? 'Obligatorio (entero)' : 'Opcional'}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Valor máximo"
                      required={isScale}
                      inputProps={isScale ? { step: 1 } : undefined}
                      value={question.maxValue ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateQuestion(qIdx, { maxValue: val === '' ? null : Number(val) });
                      }}
                      helperText={isScale ? 'Obligatorio (entero)' : 'Opcional'}
                    />
                  </Grid>
                </Grid>
              )}

              {isChoice && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {question.options.map((option, optIdx) => (
                    <Grid key={`q-${qIdx}-opt-${optIdx}`} container spacing={1}>
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Etiqueta"
                          value={option.label}
                          onChange={(e) => updateOption(qIdx, optIdx, 'label', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Valor"
                          value={option.optionValue}
                          onChange={(e) => updateOption(qIdx, optIdx, 'optionValue', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <IconButton color="error" onClick={() => removeOption(qIdx, optIdx)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                  <Box>
                    <Button size="small" onClick={() => addOption(qIdx)}>
                      Agregar opción
                    </Button>
                  </Box>
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>
      <Box>
        <Button variant="outlined" onClick={addQuestion}>
          Agregar pregunta
        </Button>
      </Box>
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Plantillas de Encuesta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Alta y edición de plantillas usadas en captura telefónica.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateForm}>
          Nueva plantilla
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading ? (
          <Typography variant="body2">Cargando plantillas...</Typography>
        ) : templates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay plantillas registradas.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {templates.map((template) => (
              <Paper key={template.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{template.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      v{template.templateVersion ?? '—'} | {template.questionCount} preguntas | id {template.id}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {template.hasClientSurveys && (
                      <Chip size="small" color="info" label="Con encuestas" />
                    )}
                    <Chip
                      size="small"
                      color={template.active ? 'success' : 'default'}
                      label={template.active ? 'Activa' : 'Inactiva'}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        void openEditForm(template.id);
                      }}
                    >
                      Editar
                    </Button>
                    <FormControlLabel
                      label=""
                      control={
                        <Switch
                          checked={template.active}
                          onChange={() => {
                            void handleToggleActive(template);
                          }}
                        />
                      }
                    />
                  </Stack>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog open={formOpen} onClose={closeForm} maxWidth="lg" fullWidth>
        <DialogTitle>
          {formMode === 'create' ? 'Nueva plantilla de encuesta' : `Editar plantilla #${editingTemplateId ?? ''}`}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Cargando plantilla...</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {formError && <Alert severity="error">{formError}</Alert>}
              {formMode === 'edit' && hasClientSurveys && (
                <Alert severity="info">
                  Esta plantilla ya tiene encuestas capturadas. Al guardar se creará una <strong>nueva versión</strong>;
                  los registros existentes no se modificarán.
                </Alert>
              )}
              {formMode === 'edit' && !hasClientSurveys && (
                <Alert severity="info">
                  Esta plantilla aún no tiene encuestas capturadas. Los cambios se guardarán sobre la misma plantilla.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Título" value={title} onChange={(e) => setTitle(e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Versión"
                    value={templateVersion}
                    onChange={(e) => setTemplateVersion(e.target.value)}
                    helperText={
                      formMode === 'edit' && hasClientSurveys
                        ? 'Opcional al versionar: el backend puede asignar la siguiente versión.'
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Vigencia desde"
                    InputLabelProps={{ shrink: true }}
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Vigencia hasta"
                    InputLabelProps={{ shrink: true }}
                    value={validTo}
                    onChange={(e) => setValidTo(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Activa"
                  />
                </Grid>
              </Grid>
              {renderQuestionFields()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!canSave || saving || loadingDetail}
            onClick={() => void handleSaveTemplate()}
          >
            {saving ? 'Guardando…' : saveButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveyTemplatesManagement;
